import PropTypes from 'prop-types'
import React from 'react'
import { FormattedMessage } from 'react-intl'
import { getData as apiGetData } from 'api/Api'
import camelCaseKeys from 'utils/camelCaseKeys'
import {
  Form,
  Button
} from 'react-bootstrap'
import Loader from 'components/Loader'
import KanBoIcon from 'components/KanBoIcon'
import Field from './Field'

export class KanBoPluginForm extends React.Component {
  static propTypes = {
    identifier: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    args: PropTypes.array,
    onChange: PropTypes.func,
    onSubmit: PropTypes.func,
    onBusyChange: PropTypes.func
  }

  static defaultProps = {
    args: []
  }

  constructor (props) {
    super(props)

    this.state = {
      isInitialized: false,
      isBusy: false,
      isSubmitting: false,
      fieldIds: [],
      fieldsById: {}
    }

    this.runningEvents = false
    this.eventsQueue = []
    this.fieldRefs = {}

    this.init = this.init.bind(this)
    this.queueEvent = this.queueEvent.bind(this)
    this.runNextEvent = this.runNextEvent.bind(this)
    this.fetchPluginForm = this.fetchPluginForm.bind(this)
    this.getArgs = this.getArgs.bind(this)
    this.makeHandleFieldRef = this.makeHandleFieldRef.bind(this)
    this.handleFieldChange = this.handleFieldChange.bind(this)
    this.handleFormSubmit = this.handleFormSubmit.bind(this)
    this.handleSubmit = this.handleSubmit.bind(this)
  }

  componentDidMount () {
    this.init()
  }

  componentDidUpdate (prevProps, prevState) {
    if (
      this.props.identifier !== prevProps.identifier ||
      this.props.type !== prevProps.type
    ) {
      this.init()
    }

    if (this.state.isBusy !== prevState.isBusy) {
      this.props.onBusyChange && this.props.onBusyChange(this.state.isBusy)
    }

    if (this.state.fieldsById !== prevState.fieldsById) {
      this.props.onChange && this.props.onChange(this.props.identifier, this.getArgs())
    }
  }

  init () {
    this.runningEvents = false
    this.eventsQueue = []
    this.fieldRefs = {}

    this.setState({
      isInitialized: false,
      isBusy: false,
      isSubmitting: false,
      fieldIds: [],
      fieldsById: {}
    }, () => {
      this.fetchPluginForm({
        Command: 'Initialize'
      }).then(() => {
        this.setState({
          isInitialized: true
        })
      })
    })
  }

  queueEvent (event) {
    this.eventsQueue.push(event)

    if (!this.runningEvents) {
      this.runNextEvent()
    }
  }

  runNextEvent () {
    const event = this.eventsQueue.shift()

    if (event) {
      this.runningEvents = true

      event().then(this.runNextEvent)
    } else {
      this.runningEvents = false
    }
  }

  fetchPluginForm (formArgs, submitResolve, submitReject) {
    const {
      identifier,
      type,
      args,
      onSubmit
    } = this.props

    this.setState({
      isBusy: true
    })

    this.fetchPluginFormPromise = apiGetData('PluginForm', {
      identifier,
      type,
      args: {
        ...formArgs,
        ...args
      }
    })

    this.fetchPluginFormPromise.then(({ Actions }) => {
      if (Actions && Actions.length) {
        let stateChanged = false
        const fieldIds = [ ...this.state.fieldIds ]
        const fieldsById = { ...this.state.fieldsById }

        let submitResolved = false

        Actions.forEach(Action => {
          if (!stateChanged) {
            switch (Action.Type) {
              case 'AddField':
              case 'RemoveField':
              case 'ReplaceField':
              case 'AddValidationError':
              case 'RemoveValidationError':
              case 'UnbindServerChangeEvent':
                stateChanged = true
                break
              default:
                break
            }
          }

          let index

          switch (Action.Type) {
            case 'AddField':
              index = fieldIds.indexOf(Action.BeforeFieldId)
              if (Action.BeforeFieldId && index >= 0) {
                fieldIds.splice(index, 0, Action.Field.Id)
              } else {
                fieldIds.push(Action.Field.Id)
              }
              fieldsById[Action.Field.Id] = camelCaseKeys(Action.Field)
              break
            case 'RemoveField':
              index = fieldIds.indexOf(Action.FieldId)
              if (index >= 0) {
                fieldIds.splice(index, 1)
              }
              delete fieldsById[Action.FieldId]
              break
            case 'ReplaceField':
              fieldsById[Action.Field.Id] = camelCaseKeys(Action.Field)
              break
            case 'AddValidationError':
              fieldsById[Action.FieldId].error = Action.Message
              break
            case 'RemoveValidationError':
              delete fieldsById[Action.FieldId].error
              break
            case 'BindServerChangeEvent':
              fieldsById[Action.FieldId].onChange = Action.MethodName
              break
            case 'UnbindServerChangeEvent':
              delete fieldsById[Action.FieldId].onChange
              break
            case 'SubmitCompleted':
              onSubmit && onSubmit(identifier, Action.Args)

              if (submitResolve) {
                submitResolve({ identifier, args: Action.Args })

                submitResolved = true
              }
              break
            default:
              break
          }
        })

        if (!submitResolved && submitReject) {
          submitReject()
        }

        if (stateChanged) {
          this.setState({
            isBusy: false,
            fieldIds,
            fieldsById
          })
        }
      }
    })

    return this.fetchPluginFormPromise
  }

  getArgs () {
    const {
      fieldIds,
      fieldsById
    } = this.state

    const args = {}

    fieldIds.forEach(fieldId => (args[fieldId] = fieldsById[fieldId].value))

    return args
  }

  makeHandleFieldRef (name) {
    const handleFieldRef = (ref) => { this.fieldRefs[name] = ref }

    return handleFieldRef.bind(this)
  }

  handleFieldChange (id, value, callback) {
    const {
      fieldsById
    } = this.state

    this.setState({
      fieldsById: {
        ...fieldsById,
        [id]: {
          ...fieldsById[id],
          value
        }
      }
    })

    if (fieldsById[id].onChange) {
      this.queueEvent(() => this.fetchPluginForm({
        ...this.getArgs(),
        Command: fieldsById[id].onChange
      }).then(callback))
    } else {
      callback()
    }
  }

  handleFormSubmit (event) {
    event.preventDefault()

    this.handleSubmit()
  }

  handleSubmit () {
    if (this.submitPromise) return this.submitPromise

    this.setState({
      isSubmitting: true
    })

    this.submitPromise = new Promise((resolve, reject) => {
      this.queueEvent(() => this.fetchPluginForm({
        ...this.getArgs(),
        Command: 'Submit'
      }, resolve, reject).finally(() => {
        this.setState({
          isSubmitting: false
        })

        delete this.submitPromise
      }))
    })

    return this.submitPromise
  }

  render () {
    const {
      onSubmit
    } = this.props
    const {
      isInitialized,
      isBusy,
      isSubmitting,
      fieldIds,
      fieldsById
    } = this.state

    const fields = fieldIds.map(fieldId => fieldsById[fieldId])

    if (onSubmit) {
      return (
        <Form
          style={{ position: 'relative' }}
          onSubmit={!isBusy ? this.handleFormSubmit : null}
        >
          {!isInitialized &&
            <Loader center />
          }
          {fields.map(field =>
            <Field
              {...field}
              key={field.id}
              ref={this.makeHandleFieldRef(field.id)}
              isFormBusy={isBusy}
              onChange={this.handleFieldChange}
            />
          )}
          {isInitialized &&
            <div>
              <Button
                disabled={isSubmitting}
                onClick={this.handleSubmit}
              >
                <FormattedMessage id='PLUGIN_FORM_SUBMIT' />
              </Button>
              {' '}
              {isSubmitting && <KanBoIcon spin name='kanbo-16-loading' />}
            </div>
          }
        </Form>
      )
    } else {
      return (
        <div>
          {!isInitialized &&
            <Loader center />
          }
          {fields.map(field =>
            <Field
              {...field}
              key={field.id}
              ref={this.makeHandleFieldRef(field.id)}
              isFormBusy={isBusy}
              onChange={this.handleFieldChange}
            />
          )}
        </div>
      )
    }
  }
}

export default KanBoPluginForm
